import { useMemo, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../ui/responsive-popover";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { featureChoices, filterFeatures, groupBySection } from "../../lib/training";

// Choose features of the Jamora catalogue (Ordre 11): active ones, plus those already chosen even if retired since — the server keeps
// an existing reference and refuses a NEW one to a retired feature.
export default function FeaturePicker({ catalog, value, onChange, disabled, testid = "feature-picker" }) {
  const [search, setSearch] = useState("");
  const choices = useMemo(() => featureChoices(catalog.features, value), [catalog.features, value]);
  const groups = useMemo(() => groupBySection(catalog.sections, filterFeatures(choices, { search, status: "" })), [catalog.sections, choices, search]);
  const toggle = (code) => onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);

  return (
    <div className="space-y-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" disabled={disabled} className="w-full justify-between font-normal" data-testid={`${testid}-open`}>
            <span className="truncate text-left text-slate-700">
              {value.length === 0 ? "Choisir des fonctionnalités" : `${value.length} fonctionnalité${value.length > 1 ? "s" : ""} choisie${value.length > 1 ? "s" : ""}`}
            </span>
            <ChevronDown size={14} className="ml-2 text-slate-400 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent title="Fonctionnalités Jamora" align="start" className="w-[22rem] p-2">
          <div className="relative mb-2">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher" className="pl-8 h-9 text-base md:text-sm"
              aria-label="Rechercher une fonctionnalité" data-testid={`${testid}-search`} />
          </div>
          <div className="max-h-72 overflow-y-auto scrollbar-thin space-y-2">
            {groups.length === 0 && <p className="text-xs text-slate-500 px-1 py-2">Aucune fonctionnalité ne correspond.</p>}
            {groups.map(({ section, features }) => (
              <div key={section.code}>
                <div className="px-1 text-2xs font-semibold uppercase tracking-wide text-slate-400">{section.label}</div>
                {features.map((f) => (
                  <label key={f.code} className="flex items-start gap-2.5 py-1.5 px-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`${testid}-option-${f.code}`}>
                    <Checkbox checked={value.includes(f.code)} onCheckedChange={() => toggle(f.code)} className="mt-0.5" />
                    <span className="text-sm text-slate-700">{f.label}{f.status === "retired" ? " (retirée)" : ""}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((code) => (
            <span key={code} className="inline-flex items-center gap-1 rounded-full bg-slate-100 pl-2.5 pr-1 py-0.5 text-xs text-slate-700">
              {catalog.featureLabel(code)}
              {!disabled && (
                <button type="button" onClick={() => toggle(code)} aria-label={`Retirer ${catalog.featureLabel(code)}`}
                  className="rounded-full p-0.5 hover:bg-slate-200"><X size={12} /></button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
